import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { DynamicTable } from '../common/DynamicTable';
import { Plus, Search, Globe } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { instanciaService } from '../../services/instanciaService';
import { comunidadeService, Comunidade } from '../../services/comunidadeService';
import { CriarComunidadeDialog } from './CriarComunidadeDialog';
import { ComunidadeDetalheDialog } from './ComunidadeDetalheDialog';

interface InstanciaOption {
  id: string;
  nome: string;
}

const TODAS_INSTANCIAS = '__todas__';

export const ComunidadesManager: React.FC = () => {
  const { toast } = useToast();
  const [instancias, setInstancias] = useState<InstanciaOption[]>([]);
  const [instanciaFiltro, setInstanciaFiltro] = useState<string>(TODAS_INSTANCIAS);

  const [comunidades, setComunidades] = useState<Comunidade[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [criarDialogOpen, setCriarDialogOpen] = useState(false);
  const [comunidadeSelecionadaId, setComunidadeSelecionadaId] = useState<string | null>(null);
  const [detalheDialogOpen, setDetalheDialogOpen] = useState(false);

  const carregarInstancias = useCallback(async () => {
    try {
      const response: any = await instanciaService.listar();
      const uazapi = (response.data.instancias || [])
        .filter((item: any) => item.tipoApi === 'UAZAPI')
        .map((item: any) => ({ id: item.id, nome: item.nome || 'Instância sem nome' }));
      setInstancias(uazapi);
    } catch {
      // silent
    }
  }, []);

  const carregarComunidades = useCallback(async () => {
    setLoading(true);
    try {
      const response = await comunidadeService.listar({
        instanciaUazapiId: instanciaFiltro === TODAS_INSTANCIAS ? undefined : instanciaFiltro,
        search: search || undefined,
        page: page + 1,
        pageSize: rowsPerPage,
      });
      setComunidades(response.data.comunidades || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar comunidades',
        description: error?.response?.data?.message || 'Não foi possível carregar a lista de comunidades.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanciaFiltro, search, page, rowsPerPage]);

  useEffect(() => { carregarInstancias(); }, [carregarInstancias]);
  useEffect(() => { carregarComunidades(); }, [carregarComunidades]);

  const instanciaNome = (id: string) => instancias.find((i) => i.id === id)?.nome || '—';

  const handleGerenciar = (comunidade: Comunidade) => {
    setComunidadeSelecionadaId(comunidade.id);
    setDetalheDialogOpen(true);
  };

  const handleComunidadeAtualizada = (comunidadeAtualizada: Comunidade) => {
    setComunidades((prev) => prev.map((c) => (c.id === comunidadeAtualizada.id ? { ...c, ...comunidadeAtualizada } : c)));
  };

  const handleComunidadeCriada = () => {
    setPage(0);
    carregarComunidades();
  };

  const columns = [
    {
      id: 'nome' as const,
      label: 'Comunidade',
      format: (_: string, row: Comunidade) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
              <Globe className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{row.nome}</p>
            {instanciaFiltro === TODAS_INSTANCIAS && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{instanciaNome(row.instanciaUazapiId || '')}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'totalGruposVinculados' as any,
      label: 'Grupos vinculados',
      align: 'center' as const,
      format: (_: number, row: Comunidade) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">{row.totalGruposVinculados ?? 0}</span>
      ),
    },
    {
      id: 'actions' as any,
      label: 'Ações',
      align: 'right' as const,
      format: (_: any, row: Comunidade) => (
        <Button size="sm" variant="outline" onClick={() => handleGerenciar(row)}>
          Gerenciar
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Comunidades</h2>
          <p className="text-sm text-muted-foreground dark:text-gray-400">
            Agrupe grupos relacionados do WhatsApp sob uma comunidade.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={instanciaFiltro} onValueChange={(v) => { setInstanciaFiltro(v); setPage(0); }}>
            <SelectTrigger className="w-[220px] bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 dark:text-white">
              <SelectValue placeholder="Instância" />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value={TODAS_INSTANCIAS}>Todas as instâncias</SelectItem>
              {instancias.map((inst) => (
                <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={() => setCriarDialogOpen(true)}
            disabled={instancias.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="mr-2 h-4 w-4" /> Nova Comunidade
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Pesquisar comunidades pelo nome..."
          className="pl-9 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
        />
      </div>

      {instancias.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground dark:text-gray-400">
          Nenhuma instância Uazapi conectada. Configure uma instância Uazapi em Configurações → Instâncias para gerenciar comunidades.
        </p>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <DynamicTable
          columns={columns}
          data={comunidades}
          loading={loading}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onSort={() => {}}
          onSearch={() => {}}
          showSearch={false}
          noDataText="Nenhuma comunidade encontrada. Crie uma nova comunidade para começar."
        />
      </div>

      <CriarComunidadeDialog
        open={criarDialogOpen}
        onOpenChange={setCriarDialogOpen}
        instancias={instancias}
        instanciaPadraoId={instanciaFiltro !== TODAS_INSTANCIAS ? instanciaFiltro : undefined}
        onCriada={handleComunidadeCriada}
      />

      <ComunidadeDetalheDialog
        open={detalheDialogOpen}
        onOpenChange={setDetalheDialogOpen}
        comunidadeId={comunidadeSelecionadaId}
        onAtualizada={handleComunidadeAtualizada}
      />
    </div>
  );
};

export default ComunidadesManager;
